import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Play, Pause, Minus, Plus, Timer, Square, Music2, Type } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ChordSheetViewer from "@/components/ChordSheetViewer";
import { fileUrl } from "@/components/FileViewer";
import { transposeChordLine, transposeKey, preferFlatsFor, sanitizeChordText } from "@/lib/chords";

/**
 * Modo Palco (ensaio) para a biblioteca pessoal — sequência em tela cheia com letras/cifras
 * grandes, navegação por toque/teclado, transpor e rolagem automática. Sem sincronização de
 * Maestro (essa é por evento, em /palco/:id). Usa o tema "stage" de alto contraste.
 */
export default function StageLite({ songs = [], open, onClose, startIndex = 0 }) {
  const [idx, setIdx] = useState(startIndex);
  const [font, setFont] = useState(26);
  const [steps, setSteps] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(40);
  const [bpm, setBpm] = useState(100);
  const [metro, setMetro] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [countIn, setCountIn] = useState(0);
  const scrollRef = useRef(null);
  const beatRef = useRef(0);
  const metroIntervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const liteTouch = useRef({ x: 0, y: 0 });
  const onLiteTouchStart = (e) => { const t = e.touches[0]; liteTouch.current = { x: t.clientX, y: t.clientY }; };
  const onLiteTouchEnd = (e) => { const t = e.changedTouches[0]; const dx = t.clientX - liteTouch.current.x, dy = t.clientY - liteTouch.current.y; if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) setIdx((i) => { const ni = i + (dx < 0 ? 1 : -1); return ni >= 0 && ni < songs.length ? ni : i; }); };

  useEffect(() => { if (open) { setIdx(startIndex); setSteps(0); setPlaying(false); } }, [open, startIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.documentElement.getAttribute("data-theme");
    document.documentElement.setAttribute("data-theme", "stage");
    return () => { if (prev) document.documentElement.setAttribute("data-theme", prev); };
  }, [open]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; setPlaying(false); setSteps(0); }, [idx]);

  useEffect(() => {
    if (!playing) return undefined;
    const el = scrollRef.current; if (!el) return undefined;
    const t = setInterval(() => { el.scrollTop += 1; }, Math.max(10, 110 - speed));
    return () => clearInterval(t);
  }, [playing, speed]);

  // ===== Metrônomo (BPM + clique sonoro + contagem 1,2,3,4) =====
  const stopInterval = () => { if (metroIntervalRef.current) { clearInterval(metroIntervalRef.current); metroIntervalRef.current = null; } };
  const playClick = (accent) => {
    const ctx = audioCtxRef.current; if (!ctx) return;
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.frequency.value = accent ? 1500 : 900;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.6, ctx.currentTime + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.05);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 0.06);
  };
  const tick = () => {
    const beat = beatRef.current;
    playClick(beat % 4 === 0);
    setPulse(true); window.setTimeout(() => setPulse(false), 90);
    setCountIn(beat < 4 ? beat + 1 : 0);
    beatRef.current = beat + 1;
  };
  const stopMetro = () => { stopInterval(); setMetro(false); setCountIn(0); setPulse(false); beatRef.current = 0; };
  const startMetro = () => {
    try { if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); audioCtxRef.current.resume(); } catch { /* audio indisponível */ }
    stopInterval(); beatRef.current = 0; setMetro(true); setCountIn(1);
    tick();
    metroIntervalRef.current = setInterval(tick, 60000 / bpm);
  };
  const toggleMetro = () => { if (metro) stopMetro(); else startMetro(); };

  useEffect(() => {
    if (!metro) return undefined;
    stopInterval();
    metroIntervalRef.current = setInterval(tick, 60000 / bpm);
    return undefined;
  }, [bpm]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { stopMetro(); }, [idx, open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => stopInterval(), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => Math.min(songs.length - 1, i + 1));
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, songs.length, onClose]);

  if (!open || songs.length === 0) return null;
  const song = songs[Math.min(idx, songs.length - 1)] || songs[0];
  const pf = preferFlatsFor(song.key || "");
  const displayKey = song.key ? transposeKey(song.key, steps, pf) : "";
  const isFile = !!song.file_id;
  const structured = !isFile && song.structured && Array.isArray(song.structured?.sections) && song.structured.sections.length ? song.structured : null;
  const showTom = !structured && !isFile;

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black flex flex-col stage-screen" data-testid="stage-lite">
      {/* barra única — cabeçalho + controles */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/15 bg-[#0a0a0a]" data-testid="stage-lite-controls">
        <button data-testid="stage-lite-exit" onClick={onClose} aria-label="Sair do modo palco" className="flex items-center gap-1 text-sm text-zinc-300 hover:text-white shrink-0 min-h-[40px]"><ArrowLeft className="h-5 w-5" /><span className="hidden md:inline">Sair</span></button>
        <div className="min-w-0 flex-1 text-center">
          <div className="font-head font-bold text-sm sm:text-lg truncate text-white">{song.title}{songs.length > 1 && <span className="text-[10px] font-mono text-zinc-500 ml-2" data-testid="stage-lite-progress">{Math.min(idx, songs.length - 1) + 1}/{songs.length}</span>}</div>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="stage-lite-scroll-trigger" aria-label="Rolagem automática" className={`h-10 w-10 grid place-items-center rounded-lg hover:bg-white/10 data-[state=open]:bg-white/10 transition-colors ${playing ? "text-primary" : "text-zinc-100"}`}>{playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}</button>
          </PopoverTrigger>
          <PopoverContent align="center" sideOffset={8} className="w-64 p-3 z-[80] bg-[#111114] border-white/15 text-white rounded-xl">
            <button data-testid="stage-lite-play" onClick={() => setPlaying((p) => !p)} className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary hover:opacity-90 text-white py-2 font-semibold mb-3">{playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}{playing ? "Pausar rolagem" : "Rolar automaticamente"}</button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400 uppercase tracking-wide w-16">Velocidade</span>
              <input type="range" min="10" max="100" value={speed} onChange={(e) => setSpeed(parseInt(e.target.value))} className="flex-1 h-2 accent-primary" aria-label="Velocidade da rolagem" />
            </div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="stage-metro-trigger" aria-label="Metrônomo" className={`relative h-10 w-10 grid place-items-center rounded-lg hover:bg-white/10 data-[state=open]:bg-white/10 transition-colors ${metro ? "text-primary" : "text-zinc-100"}`}><Timer className="h-5 w-5" /><span className={`absolute right-1 top-1 h-1.5 w-1.5 rounded-full transition-transform ${pulse ? "bg-accent scale-150" : metro ? "bg-primary" : "bg-transparent"}`} data-testid="stage-metro-pulse" aria-hidden="true" /></button>
          </PopoverTrigger>
          <PopoverContent align="center" sideOffset={8} className="w-auto p-3 z-[80] bg-[#111114] border-white/15 text-white rounded-xl" data-testid="stage-metro">
            <button data-testid="stage-metro-toggle" onClick={toggleMetro} className={`w-full flex items-center justify-center gap-2 rounded-lg py-2 font-semibold mb-3 ${metro ? "bg-primary text-white" : "border border-white/20 text-white hover:bg-white/10"}`}>{metro ? <Square className="h-4 w-4" /> : <Timer className="h-4 w-4" />}{metro ? "Parar metrônomo" : "Iniciar metrônomo"}</button>
            <div className="flex items-center justify-center gap-3">
              <button data-testid="stage-bpm-down" onClick={() => setBpm((b) => Math.max(40, b - 5))} aria-label="Diminuir BPM" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10"><Minus className="h-5 w-5" /></button>
              <div className="text-center leading-none w-14"><div className="font-mono font-bold text-white text-xl" data-testid="stage-bpm-val">{bpm}</div><div className="text-[9px] uppercase tracking-wider text-zinc-500">BPM</div></div>
              <button data-testid="stage-bpm-up" onClick={() => setBpm((b) => Math.min(240, b + 5))} aria-label="Aumentar BPM" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10"><Plus className="h-5 w-5" /></button>
            </div>
          </PopoverContent>
        </Popover>

        {showTom && (
          <>
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="stage-lite-tom-trigger" aria-label="Tom" className={`h-10 min-w-[42px] px-1.5 grid place-items-center rounded-lg hover:bg-white/10 data-[state=open]:bg-white/10 transition-colors font-mono font-bold text-lg leading-none ${displayKey ? "text-accent" : "text-zinc-400"}`}>{displayKey || "♪"}</button>
              </PopoverTrigger>
              <PopoverContent align="center" sideOffset={8} className="w-auto p-3 z-[80] bg-[#111114] border-white/15 text-white rounded-xl" data-testid="stage-lite-tom">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 text-center mb-2">Tom</div>
                <div className="flex items-center justify-center gap-3">
                  <button data-testid="stage-lite-transpose-down" onClick={() => setSteps((s) => s - 1)} aria-label="Baixar meio tom" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10"><Minus className="h-5 w-5" /></button>
                  <span className="font-mono text-2xl w-12 text-center text-accent">{steps > 0 ? `+${steps}` : steps}</span>
                  <button data-testid="stage-lite-transpose-up" onClick={() => setSteps((s) => s + 1)} aria-label="Subir meio tom" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10"><Plus className="h-5 w-5" /></button>
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="stage-lite-font-trigger" aria-label="Tamanho da fonte" className="h-10 w-10 grid place-items-center rounded-lg text-zinc-100 hover:bg-white/10 data-[state=open]:bg-white/10 transition-colors"><Type className="h-5 w-5" /></button>
              </PopoverTrigger>
              <PopoverContent align="center" sideOffset={8} className="w-auto p-3 z-[80] bg-[#111114] border-white/15 text-white rounded-xl" data-testid="stage-lite-font">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 text-center mb-2">Fonte</div>
                <div className="flex items-center justify-center gap-3">
                  <button data-testid="stage-lite-font-down" onClick={() => setFont((f) => Math.max(16, f - 2))} aria-label="Diminuir fonte" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10"><Minus className="h-5 w-5" /></button>
                  <span className="font-mono text-xl w-10 text-center text-white">{font}</span>
                  <button data-testid="stage-lite-font-up" onClick={() => setFont((f) => Math.min(56, f + 2))} aria-label="Aumentar fonte" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10"><Plus className="h-5 w-5" /></button>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
        </div>
      </div>

      {songs.length > 1 && (
        <>
          <button data-testid="stage-lite-prev" disabled={idx <= 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} aria-label="Música anterior" className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-30 h-11 w-11 grid place-items-center rounded-full bg-black/40 backdrop-blur-sm text-white/50 hover:text-white hover:bg-black/70 border border-white/10 disabled:opacity-0 disabled:pointer-events-none transition-colors"><ChevronLeft className="h-6 w-6" /></button>
          <button data-testid="stage-lite-next" disabled={idx >= songs.length - 1} onClick={() => setIdx((i) => Math.min(songs.length - 1, i + 1))} aria-label="Próxima música" className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-30 h-11 w-11 grid place-items-center rounded-full bg-black/40 backdrop-blur-sm text-white/50 hover:text-white hover:bg-black/70 border border-white/10 disabled:opacity-0 disabled:pointer-events-none transition-colors"><ChevronRight className="h-6 w-6" /></button>
        </>
      )}

      {isFile ? (
        <div className="flex-1 overflow-auto bg-black flex items-center justify-center p-2" data-testid="stage-lite-file" onTouchStart={onLiteTouchStart} onTouchEnd={onLiteTouchEnd}>
          {song.file_kind === "image" ? <img src={fileUrl(song.file_id)} alt={song.title} className="max-h-full max-w-full object-contain" />
            : song.file_kind === "pdf" ? <iframe title={song.title} src={fileUrl(song.file_id)} className="w-full h-full bg-white" />
              : <a href={fileUrl(song.file_id)} target="_blank" rel="noreferrer" className="text-accent underline">Abrir documento</a>}
        </div>
      ) : structured ? (
        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="stage-lite-structured" onTouchStart={onLiteTouchStart} onTouchEnd={onLiteTouchEnd}>
          <ChordSheetViewer structured={song.structured} music={song.title} artist={song.artist} stage />
        </div>
      ) : (
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8" data-testid="stage-lite-lyrics" onTouchStart={onLiteTouchStart} onTouchEnd={onLiteTouchEnd}>
          <pre className="whitespace-pre-wrap font-mono text-zinc-100 leading-relaxed" style={{ fontSize: `${font}px` }}>
            {(song.lyrics || "Sem cifra/letra cadastrada para esta música.").split("\n").map((l, i) => {
              const tokens = l.trim().split(/\s+/).filter(Boolean);
              const isChord = tokens.length > 0 && tokens.every((t) => /^[A-G](#|b)?(m|maj|min|dim|aug|sus|add|\d|\/|[A-G]|#|b)*$/.test(t));
              const line = (steps && isChord) ? transposeChordLine(sanitizeChordText(l), steps, pf) : l;
              return <div key={`${i}:${l.slice(0, 12)}`} className={isChord ? "text-primary font-semibold" : ""}>{line || "\u00A0"}</div>;
            })}
          </pre>
        </div>
      )}

      {countIn > 0 && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center" data-testid="stage-count-in">
          <div className="font-head font-black text-accent/90 leading-none drop-shadow-2xl" style={{ fontSize: "26vw" }}>{countIn}</div>
        </div>
      )}
    </div>,
    document.body
  );
}
